import { Controller } from "stimulus";
import { FetchRequest } from "@rails/request.js";
import autocomplete from "autocompleter";

/**
 * Generic remote autocomplete controller using the autocompleter library.
 * Fetches results from a JSON endpoint and triggers a Turbo Stream action on selection.
 *
 * Data attributes:
 *   data-remote-autocomplete-search-url: URL to fetch search results (expects JSON array)
 *   data-remote-autocomplete-action-url: URL to call on selection (Turbo Stream)
 *   data-remote-autocomplete-query-param: Query param name for search (default: "query")
 *   data-remote-autocomplete-value-param: Param name for selected value (default: "value")
 *   data-remote-autocomplete-value-key: Key in result object to use as value (default: "gid")
 *   data-remote-autocomplete-label-key: Key in result object to display (default: "name")
 *   data-remote-autocomplete-min-length: Minimum chars before searching (default: 3)
 */
export default class extends Controller {
  static targets = ["input"];

  connect() {
    this.searchUrl = this.data.get("searchUrl");
    this.actionUrl = this.data.get("actionUrl");
    this.queryParam = this.data.get("queryParam") || "query";
    this.valueParam = this.data.get("valueParam") || "value";
    this.valueKey = this.data.get("valueKey") || "gid";
    this.labelKey = this.data.get("labelKey") || "name";
    this.minLength = parseInt(this.data.get("minLength") || "3", 10);

    this.autocomplete = autocomplete({
      input: this.inputTarget,
      minLength: this.minLength,
      debounceWaitMs: 300,
      fetch: (text, update) => {
        this.fetchResults(text, update);
      },
      onSelect: (item) => {
        this.handleSelection(item);
        this.inputTarget.value = "";
      },
      render: (item) => {
        const div = document.createElement("div");
        div.textContent = item[this.labelKey];
        div.classList.add("p-2", "cursor-pointer", "hover:bg-gray-100");
        return div;
      },
      emptyMsg: "No results found",
      className: "absolute z-40 w-full max-h-60 overflow-auto mt-0.5 rounded-md border border-gray-300 shadow-lg bg-white"
    });
  }

  disconnect() {
    if (!this.autocomplete) return;

    this.autocomplete.destroy();
  }

  async fetchResults(text, update) {
    const url = `${this.searchUrl}?${this.queryParam}=${encodeURIComponent(text)}`;

    try {
      const request = new FetchRequest("get", url, { responseKind: "json" });
      const response = await request.perform();

      if (response.ok) {
        const results = await response.json;
        update(results);
      } else {
        update([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      update([]);
    }
  }

  async handleSelection(item) {
    const value = item[this.valueKey];
    const url = `${this.actionUrl}?${this.valueParam}=${encodeURIComponent(value)}`;

    try {
      const request = new FetchRequest("get", url, { responseKind: "turbo-stream" });
      await request.perform();
    } catch (error) {
      console.error("Action error:", error);
    }
  }
}
